// src/prototype/components/ApprovalChain.tsx
// The approval & sign-off chain, in two forms:
//   full    — vertical stepper with notes, used on the Initiative Command Center
//   compact — five joined dots, used on Execution Portfolio cards
import React from 'react';
import { Check } from 'lucide-react';
import type { ApprovalStep } from '../data/executionInitiatives';

export interface ApprovalChainProps {
  steps: ApprovalStep[];
  compact?: boolean;
}

export const ApprovalChain: React.FC<ApprovalChainProps> = ({ steps, compact = false }) => {
  if (compact) {
    return (
      <ol className="flex items-center gap-1" aria-label="Approval progress">
        {steps.map((step, i) => (
          <li key={step.label} className="flex items-center gap-1" title={`${step.label} — ${step.state}`}>
            <span
              className={`w-2.5 h-2.5 rounded-full ${
                step.state === 'done'
                  ? 'bg-emerald-500'
                  : step.state === 'current'
                    ? 'bg-[#FF6E4C] ring-2 ring-[#FF6E4C]/30'
                    : 'bg-slate-200'
              }`}
            />
            {i < steps.length - 1 && (
              <span className={`w-4 h-0.5 ${step.state === 'done' ? 'bg-emerald-300' : 'bg-slate-200'}`} />
            )}
          </li>
        ))}
      </ol>
    );
  }

  return (
    <ol>
      {steps.map((step, i) => (
        <li key={step.label} className="flex gap-3">
          <div className="flex flex-col items-center">
            <span
              className={`w-6 h-6 rounded-full flex items-center justify-center shrink-0 ${
                step.state === 'done'
                  ? 'bg-emerald-500 text-white'
                  : step.state === 'current'
                    ? 'bg-[#FF6E4C] text-white'
                    : 'bg-slate-100 text-slate-400 border border-slate-200'
              }`}
            >
              {step.state === 'done' ? <Check className="w-3.5 h-3.5" /> : <span className="text-[10px] font-bold">{i + 1}</span>}
            </span>
            {i < steps.length - 1 && (
              <span className={`w-0.5 flex-1 min-h-[14px] ${step.state === 'done' ? 'bg-emerald-300' : 'bg-slate-200'}`} />
            )}
          </div>
          <div className="pb-4 min-w-0">
            <p className={`text-xs font-bold leading-snug ${step.state === 'pending' ? 'text-[#717182]' : 'text-[#18365E]'}`}>
              {step.label}
              {step.cfo && (
                <span className="ml-1.5 text-[9px] font-bold uppercase tracking-wide text-[#FF6E4C] bg-[#FFF1ED] px-1.5 py-0.5 rounded">
                  CFO gate
                </span>
              )}
            </p>
            {step.note && <p className="text-[11px] text-[#717182] mt-0.5 leading-relaxed">{step.note}</p>}
            {step.state === 'current' && (
              <span className="inline-block mt-1 text-[10px] font-bold text-[#FF6E4C] uppercase tracking-wide">In progress</span>
            )}
          </div>
        </li>
      ))}
    </ol>
  );
};

export default ApprovalChain;
